import React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Avatar, Typography } from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';

import { fToNow } from 'src/utils/format-time';

import { useTranslate } from 'src/locales';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { SafeHTML } from 'src/components/safe-html';
import { useSettingsContext } from 'src/components/settings';
import { FileThumbnail } from 'src/components/file-thumbnail';

// ----------------------------------------------------------------------

export type NotificationItemProps = {
  notification: {
    id: string;
    type: string;
    title: string;
    category: string;
    isUnRead: boolean;
    avatarUrl: string | null;
    createdAt: string | number | null;
    meta: any;
    count: number;
    primaryActor: any;
    updatedAt: string | number | null;
    profileUrl: any;
    isRead: boolean;
    notificationType: string;
  };
  handleClickNotification: () => void;
};

/** WhatsApp call notifications show a call icon instead of the contact avatar. */
const WHATSAPP_CALL_NOTIFICATION_ICONS: Record<
  string,
  { icon: string; color: 'success' | 'error' } | undefined
> = {
  WHATSAPP_CALL_INCOMING: { icon: 'solar:phone-calling-bold', color: 'success' },
  WHATSAPP_CALL_MISSED: { icon: 'material-symbols:call-missed-rounded', color: 'error' },
};

const readerContent = (data: string | React.ReactNode, sx?: any) => (
  <Typography
    sx={{
      '& p': { m: 0, typography: 'body2' },
      '& a': { color: 'inherit', textDecoration: 'none' },
      '& strong': { typography: 'subtitle2' },
      fontSize: 14,
      width: '90%',
      ...sx,
    }}
  >
    {typeof data === 'string' ? <SafeHTML html={data} /> : data}
  </Typography>
);

export function NotificationItem({ notification, handleClickNotification }: NotificationItemProps) {
  const { t } = useTranslate('translation');
  const settings = useSettingsContext();

  // ============================================================================================
  function formatTranslationWithJSX(
    template: string,
    variables: Record<string, React.ReactNode>
  ): React.ReactNode[] {
    return template.split(/({{.*?}})/g).map((part, index) => {
      const match = part.match(/{{(.*?)}}/);
      if (match) {
        const key = match[1].trim();
        return <React.Fragment key={index}>{variables[key] ?? ''}</React.Fragment>;
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  }

  function getConversationEventMessage(eventType: any) {
    const { agentName, notificationType, isRead } = eventType;
    const meta = eventType.meta || eventType.data || {};
    const { conversation } = meta;

    const contact = conversation?.contact || {};
    const channel = conversation?.channel || {};

    const displayName =
      contact.firstName || contact.lastName
        ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
        : contact.name ||
          (channel.channelType === 'WHATSAPP' && contact.phone) ||
          (channel.channelType === 'EMAIL' && contact.email) ||
          (['VOICE', 'LIVE_CHAT'].includes(channel.channelType) && contact.phone) ||
          'Unknown Contact';

    const rawChannelType = channel.channelType || 'Unknown Channel';

    const formatChannelType = (type: string) => {
      const formatted = type.replace(/_/g, ' ').toLowerCase();
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    };

    const channelType = formatChannelType(rawChannelType);

    const getStatus = (data: any) => {
      if (data?.status) {
        const value = data.status === 'OPEN' ? 'OPEN' : 'CLOSE';
        return <strong>{t(value)}</strong>;
      }
      return '';
    };

    // Format usage values nicely for SYSTEM_ALERT (e.g., storage in GB)
    const formatUsageValue = (key: string, value: number) => {
      if (key === 'storage') {
        return `${(value / 1024 ** 3).toFixed(2)} GB`;
      }
      if (key === 'did') {
        return `${value.toLocaleString()} ${t('NUMBERS')}`;
      }
      if (key === 'bundle') {
        return `${value.toLocaleString()} ${t('MINUTES')}`;
      }
      return value.toLocaleString();
    };

    // Base vars with default keys
    const vars: Record<string, any> = {
      displayName: isRead ? displayName : <strong>{displayName}</strong>,
      agentName: isRead ? agentName || t('YOU') : <strong>{agentName || t('YOU')}</strong>,
      channelType: isRead ? (
        t(rawChannelType.toLocaleUpperCase())
      ) : (
        <strong>{t(rawChannelType.toLocaleUpperCase())}</strong>
      ),
      status: getStatus(conversation),
    };

    // Add extra vars for SYSTEM_ALERT notificationType
    if (notificationType === 'SYSTEM_ALERT' && meta) {
      const { usageKey, used, limit, threshold, message } = meta;

      // Format usageKey label (capitalize first letter)
      const usageName = usageKey ? usageKey.charAt(0).toUpperCase() + usageKey.slice(1) : '';

      vars.usageName = usageName;
      vars.usedFormatted = formatUsageValue(usageKey, used);
      vars.limitFormatted = formatUsageValue(usageKey, limit);
      vars.threshold = threshold;
      vars.message = message || '';
    }

    // Determine translation key
    let translationKey = '';
    switch (notificationType) {
      case 'CONVERSATION_ASSIGNED':
        translationKey = 'CONVERSATION_ASSIGNED';
        break;
      case 'CONVERSATION_UNASSIGNED':
        translationKey = 'CONVERSATION_UNASSIGNED_NOTIFICATION';
        break;
      case 'NEW_CONVERSATION_ASSIGNED':
        translationKey = 'NEW_CONVERSATION_ASSIGNED';
        break;
      case 'NEW_MESSAGE_IN_CONVERSATION':
        translationKey = 'NEW_MESSAGE_IN_CONVERSATION';
        break;
      case 'CONVERSATION_STATUS_CHANGED':
        translationKey = 'CONVERSATION_STATUS_CHANGED';
        break;
      case 'SYSTEM_ALERT':
        translationKey = 'USAGE_THRESHOLD_REACHED';
        break;
      case 'MENTIONED_IN_MESSAGE':
        translationKey = 'MENTIONED_IN_MESSAGE';
        break;
      case 'FRIEND_ADDED':
        translationKey = meta?.message || 'FRIEND_ADDED';
        break;
      case 'WHATSAPP_CALL_INCOMING':
      case 'WHATSAPP_CALL_MISSED':
        // Saved notifications may carry only the ready-made text (meta.message), no contact
        if (!conversation?.contact && meta?.message) {
          return <>{meta.message}</>;
        }
        translationKey =
          notificationType === 'WHATSAPP_CALL_INCOMING'
            ? 'WHATSAPP_CALL_NOTIFICATION_INCOMING'
            : 'WHATSAPP_CALL_NOTIFICATION_MISSED';
        break;
      default:
        return <>{t('UNKNOWN_EVENT')}</>;
    }

    const template = t(translationKey); // string with placeholders like {{usageName}}, {{used}}, etc.

    return <>{formatTranslationWithJSX(template, vars)}</>;
  }

  // ============================================================================================
  const isWhatsAppCallNotification = WHATSAPP_CALL_NOTIFICATION_ICONS[notification?.notificationType];

  const renderAvatar = () =>
    isWhatsAppCallNotification ? (
      <ListItemAvatar>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            color: 'common.white',
            bgcolor: `${isWhatsAppCallNotification.color}.main`,
          }}
        >
          <Iconify icon={isWhatsAppCallNotification.icon} width={18} />
        </Avatar>
      </ListItemAvatar>
    ) : (
    <ListItemAvatar>
      <Avatar
        alt={
          notification?.meta?.conversation?.contact?.firstName ||
          notification?.meta?.conversation?.contact?.lastName
        }
        src={
          notification?.primaryActor?.ProfileURL ||
          notification?.profileUrl ||
          notification?.meta?.conversation?.contact?.firstName ||
          notification?.meta?.conversation?.contact?.lastName ||
          'Unknown'
        }
        sx={{ width: 32, height: 32 }}
      />
    </ListItemAvatar>
  );

  const renderText = () => (
    <ListItemText
      primary={readerContent(
        getConversationEventMessage(notification),
        settings.state.direction === 'rtl' && { marginRight: 'auto' }
      )}
      secondary={<>{fToNow(notification.updatedAt)}</>}
      slotProps={{
        primary: {
          sx: {
            mb: 0.5,
            fontWeight: 200,
            pl: 2,
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        },
        secondary: {
          sx: {
            gap: 0.5,
            display: 'flex',
            alignItems: 'center',
            typography: 'caption',
            color: 'text.disabled',
          },
        },
      }}
    />
  );

  const renderUnReadBadge = () => (
    <Box
      sx={{
        top: 26,
        width: 8,
        height: 8,
        right: 20,
        borderRadius: '50%',
        bgcolor: 'success.main',
        position: 'absolute',
      }}
    />
  );

  const renderFriendAction = () => (
    <Box sx={{ gap: 1, mt: 1.5, display: 'flex' }}>
      <Button size="small" variant="contained">
        Accept
      </Button>
      <Button size="small" variant="outlined">
        Decline
      </Button>
    </Box>
  );

  const renderProjectAction = () => (
    <>
      <Box
        sx={{
          p: 1.5,
          my: 1.5,
          borderRadius: 1.5,
          color: 'text.secondary',
          bgcolor: 'background.neutral',
        }}
      >
        {readerContent(
          `<p><strong>@Jaydon Frankie</strong> feedback by asking questions or just leave a note of appreciation.</p>`
        )}
      </Box>

      <Button size="small" variant="contained" sx={{ alignSelf: 'flex-start' }}>
        Reply
      </Button>
    </>
  );

  const renderFileAction = () => (
    <Box
      sx={(theme) => ({
        p: theme.spacing(1.5, 1.5, 1.5, 1),
        gap: 1,
        mt: 1.5,
        display: 'flex',
        borderRadius: 1.5,
        bgcolor: 'background.neutral',
      })}
    >
      <FileThumbnail file="http://localhost:8080/httpsdesign-suriname-2015.mp3" />

      <ListItemText
        primary="design-suriname-2015.mp3 design-suriname-2015.mp3"
        secondary="2.3 Mb"
        slotProps={{
          primary: {
            noWrap: true,
            sx: (theme) => ({
              color: 'text.secondary',
              fontSize: theme.typography.pxToRem(13),
            }),
          },
          secondary: {
            sx: {
              mt: 0.25,
              typography: 'caption',
              color: 'text.disabled',
            },
          },
        }}
      />

      <Button size="small" variant="outlined" sx={{ flexShrink: 0 }}>
        Download
      </Button>
    </Box>
  );

  const renderTagsAction = () => (
    <Box
      sx={{
        mt: 1.5,
        gap: 0.75,
        display: 'flex',
        flexWrap: 'wrap',
      }}
    >
      <Label variant="outlined" color="info">
        Design
      </Label>
      <Label variant="outlined" color="warning">
        Dashboard
      </Label>
      <Label variant="outlined">Design system</Label>
    </Box>
  );

  const renderPaymentAction = () => (
    <Box sx={{ gap: 1, mt: 1.5, display: 'flex' }}>
      <Button size="small" variant="contained">
        Pay
      </Button>
      <Button size="small" variant="outlined">
        Decline
      </Button>
    </Box>
  );

  return (
    <ListItemButton
      disableRipple
      sx={[
        (theme) => ({
          p: 1.5,
          alignItems: 'center',
          borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
          direction: settings.state.direction,
          flexDirection: settings.state.direction === 'ltr' ? 'row' : 'row-reverse',
        }),
      ]}
      onClick={
        notification?.notificationType === 'SYSTEM_ALERT' ? undefined : handleClickNotification
      }
    >
      {!notification?.isRead && renderUnReadBadge()}
      {renderAvatar()}

      <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
        {renderText()}
        {notification.type === 'friend' && renderFriendAction()}
        {notification.type === 'project' && renderProjectAction()}
        {notification.type === 'file' && renderFileAction()}
        {notification.type === 'tags' && renderTagsAction()}
        {notification.type === 'payment' && renderPaymentAction()}
      </Box>
    </ListItemButton>
  );
}
